'use client';
import { APP_URL, SCORM_WRAPPER_PATH } from "../../config";
import MainCard from "../../components/cards/MainCard";
import { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useLazyGetCourseByIdQuery } from "../../store/services/university-service";
import { CircularProgress, Typography, Alert, Snackbar, Box } from "@mui/material";

export default function TakeCourse() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const email = searchParams.get('emailAddress');
    const [courseData, setCourseData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [getCourseById] = useLazyGetCourseByIdQuery();
    const [notification, setNotification] = useState(null);
    const [apiReady, setApiReady] = useState(false);
    const iframeRef = useRef(null);

    const path = window.location.pathname;
    const encodedCourseId = path.split('takecourse/')[1]?.split('?')[0];
    const courseId = decodeURIComponent(encodedCourseId).replace('+', ' ');

    // Set API endpoint for SCORM runtime
    useEffect(() => {
        window.REACT_APP_RESPONSE_API_URL = process.env.REACT_APP_RESPONSE_API_URL;
        setApiReady(true);
    }, []);

    // Handle messages from SCORM content
    useEffect(() => {
        const handleMessage = (event) => {
            if (event.data && event.data.type === 'scorm-complete') {
                setNotification({
                    type: 'success',
                    message: 'Course completed successfully!'
                });
            }

            if (event.data && event.data.type === 'scorm-interaction') {
                setNotification({
                    type: 'info',
                    message: `Question answered: ${event.data.data.correct ? 'Correct' : 'Incorrect'}`
                });
            }

            if (event.data && event.data.type === 'scorm-report-status') {
                setNotification({
                    type: event.data.status === 'success' ? 'success' : 'error',
                    message: event.data.message
                });
            }

            // The wrapper's "Back to courses" button after a dispatch course is done
            if (event.data && event.data.type === 'scorm-dispatch-done') {
                navigate('/');
            }
        };

        window.addEventListener('message', handleMessage);
        return () => {
            window.removeEventListener('message', handleMessage);
        };
    }, [navigate]);

    // Show report status
    useEffect(() => {
        window.showReportStatus = (status, message) => {
            if (status === 'success') {
                setNotification({
                    type: 'success',
                    message: 'Score reported successfully!'
                });
            } else if (status === 'error') {
                console.error('Error reporting score:', message);
                setNotification({
                    type: 'error',
                    message: `Failed to report score: ${message}`
                });
            }
        };

        return () => {
            delete window.showReportStatus;
        };
    }, []);

    useEffect(() => {
        if (!email) {
            navigate('/');
            return;
        }

        const fetchCourseData = async () => {
            try {
                setIsLoading(true);
                setError(null);

                const response = await getCourseById({
                    courseId: courseId,
                    includes: ""
                }).unwrap();

                if (response.status === "rejected") {
                    throw new Error("Failed to fetch course content");
                }

                if (response.isLms === true && response.internalId) {
                    navigate(
                        `/takescormcourse/${response.internalId}?emailAddress=${encodeURIComponent(email)}&universityCourseId=${response.internalId}`,
                        { replace: true }
                    );
                    return;
                }

                setCourseData(response);
                setIsLoading(false);
            } catch (err) {
                setError(err.message || "Failed to load course");
                console.error("Error fetching course:", err);
                setIsLoading(false);
            }
        };

        if (courseId) {
            fetchCourseData();
        }
    }, [courseId, getCourseById, email, navigate]);

    if (!email) {
        return null;
    }

    if (isLoading) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white" }} border={false} boxShadow={true}>
                <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80vh">
                    <CircularProgress />
                    <Typography variant="h6" mt={2}>
                        Loading course content...
                    </Typography>
                </Box>
            </MainCard>
        );
    }

    if (error || !courseData) {
        return (
            <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white" }} border={false} boxShadow={true}>
                <Box display="flex" alignItems="center" justifyContent="center" height="80vh" p={3}>
                    <Alert severity="error" sx={{ maxWidth: 600 }}>
                        <Typography variant="h6">Error Loading Course</Typography>
                        <Typography>{error || "Failed to load course content"}</Typography>
                    </Alert>
                </Box>
            </MainCard>
        );
    }

    // Check if this is an LMS/SCORM course
    const isLmsCourse = courseData.isLms === true;

    // Generate appropriate course URL
    const getCourseUrl = () => {
        if (isLmsCourse) {
            // For LMS courses, use the SCORM wrapper
            const absoluteCourseUrl = `${APP_URL}/${courseData.courseContentPath}`;
            return `${SCORM_WRAPPER_PATH}?` +
                `courseUrl=${encodeURIComponent(absoluteCourseUrl)}&` +
                `emailAddress=${encodeURIComponent(email)}&` +
                `courseIdentifier=${encodeURIComponent(courseId)}&` +
                `courseType=${courseData.isXApi ? 'xapi' : 'scorm'}`;
        } else {
            // For standard courses, use the direct URL
            return `${APP_URL}/${courseData.courseContentPath}?useremailaddress=${email}&courseidentifier=${courseId}`;
        }
    };

    const courseUrl = getCourseUrl();

    return (
        <>
            <MainCard content={false} sx={{ width: "100%", bgcolor: "common.white" }} border={false} boxShadow={true}>
                {apiReady || !isLmsCourse ? (
                    <iframe
                        ref={iframeRef}
                        style={{ width: "100%", height: "100vh", border: "none" }}
                        title={`course-${courseId}`}
                        src={courseUrl}
                        allow={isLmsCourse ? "autoplay; fullscreen" : undefined}
                        allowFullScreen={isLmsCourse}
                    />
                ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="80vh">
                        <CircularProgress />
                        <Typography variant="h6" mt={2}>
                            Initialising SCORM runtime...
                        </Typography>
                    </Box>
                )}
            </MainCard>

            {/* Notification Snackbar for LMS courses */}
            {isLmsCourse && notification && (
                <Snackbar
                    open={!!notification}
                    autoHideDuration={6000}
                    onClose={() => setNotification(null)}
                    anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => setNotification(null)}
                        severity={notification.type}
                        sx={{ width: '100%' }}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            )}
        </>
    );
}